import { Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';

/**
 * ServiceChargeNonRecoverables Widget
 */
@Component({
  selector: 'service-charge-non-recoverables-widget',
  templateUrl: './service-charge-non-recoverables-widget.component.html',
  styleUrls: ['./service-charge-non-recoverables-widget.component.scss']
})
export class ServiceChargeNonRecoverablesWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
  * Constructor
  */
  constructor(injector: Injector) {
    super(injector, 'serviceChargeNonRecoverables', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler('serviceChargeNonRecoverables', this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }
    this.currentLayout = this.widgetConfig.widgetSettings.currentLayout;
    if (!this.currentLayout) {
      this.currentLayout = 'Default';
    }
    this.availableLayouts = this.widgetConfig.widgetSettings.availableLayouts;

    this.mockupData$ = this.widgetService.loadMockup('serviceChargeNonRecoverables').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('serviceChargeNonRecoverables', this.getReport()).pipe();

  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  preprocessLoadedData(data) {
    // percentages are in form of 0.13 for 13% => convert
    this.percentageToUI(data, "managementCostsServiceChargePercentOfERV");
    this.percentageToUI(data, "managementCostsNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "managementCostsNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "managementCostsQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "maintenanceCostsServiceChargePercentOfERV");
    this.percentageToUI(data, "maintenanceCostsNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "maintenanceCostsNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "maintenanceCostsQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "otherCostsServiceChargePercentOfERV");
    this.percentageToUI(data, "otherCostsNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "otherCostsNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "otherCostsQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "insuranceCostsServiceChargePercentOfERV");
    this.percentageToUI(data, "insuranceCostsNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "insuranceCostsNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "insuranceCostsQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "groundTaxServiceChargePercentOfERV");
    this.percentageToUI(data, "groundTaxNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "groundTaxNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "groundTaxQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "vacancyCostsServiceChargePercentOfERV");
    this.percentageToUI(data, "vacancyCostsNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "vacancyCostsNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "vacancyCostsQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "leaseholdCostsServiceChargePercentOfERV");
    this.percentageToUI(data, "leaseholdCostsNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "leaseholdCostsNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "leaseholdCostsQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "structuralVacancyCostsServiceChargePercentOfERV");
    this.percentageToUI(data, "structuralVacancyCostsNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "structuralVacancyCostsNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "structuralVacancyCostsQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "otherSpecificCostsServiceChargePercentOfERV");
    this.percentageToUI(data, "otherSpecificCostsNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "otherSpecificCostsNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "otherSpecificCostsQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "statutoryExpensesServiceChargePercentOfERV");
    this.percentageToUI(data, "statutoryExpensesNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "statutoryExpensesNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "statutoryExpensesQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "nonRecoverableExpensesServiceChargePercentOfERV");
    this.percentageToUI(data, "nonRecoverableExpensesNonRecoverableChargePercentOfERV");
    this.percentageToUI(data, "nonRecoverableExpensesNonRecoverableChargePercentOfERVArgus");
    this.percentageToUI(data, "nonRecoverableExpensesQuotePartNotRecoveredPercent");

    this.percentageToUI(data, "totalAmountErvShare");
    this.percentageToUI(data, "totalAmountNonRecoverableErvShare");
    this.percentageToUI(data, "totalAmountNonRecoverableErvShareArgus");
    this.percentageToUI(data, "totalAmountQuotePartNotRecovered");


    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    const data = ObjectUtil.deepCopy(this.widgetData);
    return data;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  public layoutChanged(newLayout: string): void {
    try {
      if (this.widgetConfig?.widgetSettings?.layoutSizeConfiguration) {
        const config = this.widgetConfig.widgetSettings.layoutSizeConfiguration[newLayout];
        if (config.x) {
          this.widgetConfig.minSizeX = config.x;
          this.widgetConfig.sizeX = config.x;

        }
        if (config.y) {
          this.widgetConfig.sizeY = config.y;
        }
      }
      this.widgetConfig.widgetSettings.currentLayout = newLayout;
    } catch (e) {
      // Nothing
    }
  }
}
